import { initializeTable } from '../tables';
import RowContextMenuButtonColumn from '../formatters/RowContextMenuButtonColumn';
import { getCookie, hasPermission, escapeHtml } from '../../utils';
import { addAlert } from '../../alerts';
import { deletePrompt, makeOpenDeleteModal } from '../../prompts.js';
import { printLabel } from '../../labels';

async function deleteComponent(rowData, row) {
    let info;
    try {
        const response = await fetch(rowData.delete_info_url, {
            headers: {
                'X-CSRFTOKEN': getCookie('csrftoken'),
            },
        });
        if (!response.ok) throw new Error(response.statusText);
        info = await response.json();
    } catch (e) {
        addAlert(
            'error',
            gettext(
                'Wystąpił błąd podczas pobierania informacji o podzespole.',
            ),
        );
        return;
    }

    let sections = '';

    if (info.manual_executions.length > 0) {
        const items = info.manual_executions
            .map((ex) => {
                const status = ex.is_finished
                    ? gettext('zakończony')
                    : gettext('w toku');
                return `<ul class="delete-process-item">
                    <li><span class="delete-detail-label">${gettext('Instrukcja')}</span>${escapeHtml(ex.manual)}</li>
                    <li><span class="delete-detail-label">${gettext('Użytkownik')}</span>${escapeHtml(ex.user)}</li>
                    <li><span class="delete-detail-label">${gettext('Data')}</span>${escapeHtml(ex.start_date)}</li>
                    <li><span class="delete-detail-label">${gettext('Status')}</span>${status}</li>
                </ul>`;
            })
            .join('');
        sections += `<p><strong>${gettext('Procesy')} (${info.manual_executions.length}):</strong></p>${items}`;
    }

    if (info.measurements_by_type.length > 0) {
        const items = info.measurements_by_type
            .map(
                (m) =>
                    `<li><span class="delete-detail-label">${escapeHtml(m.name)}</span>${m.count}</li>`,
            )
            .join('');
        sections += `<p><strong>${gettext('Pomiary')}:</strong></p><ul>${items}</ul>`;
    }

    if (info.offer) {
        sections += `<p><strong>${gettext('Oferta sklepowa')}</strong></p>
            <ul>
                <li><span class="delete-detail-label">${gettext('Tytuł oferty')}</span>${escapeHtml(info.offer.title)}</li>
                <li><span class="delete-detail-label">${gettext('Produkt')}</span>${info.offer.woocommerce_id ?? '—'}</li>
                <li><span class="delete-detail-label">${gettext('Status')}</span>${escapeHtml(info.offer.status)}</li>
            </ul>`;
    }

    if (info.photos.length > 0) {
        const items = info.photos
            .map((name) => `<li>${escapeHtml(name)}</li>`)
            .join('');
        sections += `<p><strong>${gettext('Zdjęcia')} (${info.photos.length}):</strong></p><ul>${items}</ul>`;
    }

    const warningHtml = sections
        ? `<p class="delete-details-intro">${gettext('Zostaną również usunięte:')}</p><div class="delete-details-box">${sections}</div>`
        : '';

    const popupClass = sections
        ? 'modal__content delete-modal'
        : 'modal__content delete-modal delete-modal--compact';

    deletePrompt({
        options: {
            title: gettext('Uwaga!'),
            html: `<p class="delete-question">${gettext('Czy na pewno chcesz usunąć ten podzespół?')}</p><p class="delete-item-info">${escapeHtml(rowData.enova_id)}&nbsp;&nbsp;${escapeHtml(rowData.enova_data?.group)}&nbsp;&nbsp;${escapeHtml(rowData.enova_data?.type)}</p>${warningHtml}<p class="delete-warning">${gettext('Ta operacja jest nieodwracalna i może spowodować niespójność z bazą Enova.')}</p>`,
            width: 670,
            confirmText: gettext('Usuń'),
            showCancelButton: false,
            customClass: {
                popup: popupClass,
            },
            didOpen: makeOpenDeleteModal(gettext('Usuwanie podzespołu')),
        },
        url: rowData.delete_url,
        successAction: () => {
            addAlert('success', gettext('Podzespół został usunięty.'));
            row.delete();
        },
        failedAction: (error) => {
            const message =
                error?.data?.message ||
                error?.message ||
                gettext('Wystąpił błąd podczas usuwania.');
            addAlert('error', message);
        },
    });
}

export default function initializeComponentListTable({ ...options }) {
    const rootElement = document.querySelector('#componentListTable');

    const forcedFilter = [];
    const pillsContainerSelector = '.active-filters-container';
    const canViewCatalog = rootElement.dataset.userCanViewCatalog === 'true';
    const canDelete = hasPermission('users.delete_components');
    const printMethod = rootElement.dataset.printMethod;
    const printConfig = printMethod
        ? {
              method: printMethod,
              printUrl: rootElement.dataset.printUrl,
              zplUrl: rootElement.dataset.zplUrl,
              sdkUrl: rootElement.dataset.sdkUrl,
          }
        : null;

    initializeTable({
        tableElement: rootElement,
        filtersElement: document.querySelector(
            '#componentListFilters .form-fields',
        ),
        tabsElement: document.querySelector('.table-tabs'),
        ajaxURL: rootElement.dataset.ajaxUrl,
        forcedFilter: forcedFilter,
        pillsConfig: {
            containerSelector: pillsContainerSelector,
            ignoreFields: [],
        },
        rowContextMenu: function (e, row) {
            const rowData = row.getData();
            let menu = [
                {
                    label: gettext('Szczegóły'),
                    action: function (e, row) {
                        window.location.href = rowData.detail_url;
                    },
                },
                {
                    label: gettext('Katalogowanie podzespołów'),
                    action: function (e, row) {
                        window.location.href = rowData.catalog_url;
                    },
                    disabled: !canViewCatalog,
                },
                printConfig
                    ? {
                          label: gettext('Drukuj kod kreskowy'),
                          action: () =>
                              printLabel(rowData.enova_id, printConfig),
                      }
                    : null,
                canDelete
                    ? {
                          label: gettext('Usuń'),
                          action: (e, row) => deleteComponent(rowData, row),
                      }
                    : null,
            ];
            menu = menu.filter((item) => item);
            return menu;
        },
        columns: [
            { title: '#', formatter: 'rownum', width: 60, headerSort: false },
            {
                title: gettext('Kod'),
                field: 'enova_id',
                headerSortFieldName: 'enova_id',
                resizable: false,
            },
            {
                title: gettext('Grupa'),
                field: 'enova_data.group',
                headerSortFieldName: 'enova_data__group',
                resizable: false,
            },
            {
                title: gettext('Typ'),
                field: 'enova_data.type',
                headerSortFieldName: 'enova_data__type',
                resizable: false,
            },
            {
                title: gettext('Producent'),
                field: 'enova_data.manufacturer',
                headerSortFieldName: 'enova_data__manufacturer',
                resizable: false,
            },
            {
                title: gettext('Model'),
                field: 'enova_data.model',
                headerSortFieldName: 'enova_data__model',
                resizable: false,
            },
            {
                title: gettext('Akcja'),
                resizable: false,
                headerSort: false,
                width: 100,
                formatter: RowContextMenuButtonColumn,
            },
        ],
        ...options,
    });
}
